<template>
    <div class="container-fluid">
        <div class="row justify-content-center">
            <div class="col-12 admin-table-col">
                <!-- Header -->
                <br />
                <br />
                <div v-if="isViewingPageReports">
                    <div class="row">
                        <div class="col-12">
                            <router-link :to="{ name: 'Pages' }">Pages</router-link>
                            <span class="text-secondary mx-2">/</span>
                            <span>{{ page.name }}</span>
                        </div>
                    </div>
                </div>
                <div v-else class="row">
                    <div class="col-12">
                        <h4 class="mb-2 mt-3 font-weight-bolder">Reports</h4>
                        <p class="text-secondary mb-2 medium">
                            Generate a report for a learning experience and timeframe.
                            <a href="#" @click="openInfoModal()">Learn more about report types.</a>
                        </p>
                    </div>
                </div>
                <br />
                <hr />
                <br />
                <!-- Report Config Controls -->
                <div class="row">
                    <div class="col-12" style="max-width: 640px">
                        <div class="row align-items-center mb-3">
                            <div class="col-12 col-sm-3">Report Type</div>
                            <div class="col-12 col-sm-9">
                                <report-type-selector v-model="reportQuery.reportType"></report-type-selector>
                            </div>
                        </div>
                        <div class="row d-flex align-items-center mb-3" v-if="showLibraryItemSelector">
                            <div class="col-12 col-sm-3 col-form-label">Learning Experience</div>
                            <div class="col-12 col-sm-9">
                                <library-item-selector
                                    v-model="selectedItem"
                                    :item-types="['owned_case', 'community_reference']"
                                    placeholder="Select a learning experience..."
                                    emptyText="No learning experiences found matching your search."
                                    :page-id="pageIDValidated"
                                    ref="itemSelector"
                                ></library-item-selector>
                            </div>
                        </div>
                        <div
                            v-if="['choiceResponse', 'textResponse'].includes(reportQuery.reportType)"
                            class="row d-flex align-items-center mb-3"
                        >
                            <div class="col-12 col-sm-3">Inquiry</div>
                            <div class="col-12 col-sm-9">
                                <report-panel-selector
                                    v-model="reportQuery.panelId"
                                    :options="selectedReportPanelOptions"
                                ></report-panel-selector>
                            </div>
                        </div>
                        <base-form
                            v-model="reportQuery"
                            :fields="reportFields"
                            :inline-style="true"
                            ref="reportQueryForm"
                        ></base-form>
                        <div class="d-flex justify-content-end">
                            <div class="mr-1" v-show="showBulkExportBtn">
                                <form method="GET" action="/api/reports/choice/export/">
                                    <input
                                        key="organizationId"
                                        hidden
                                        style="display: none"
                                        name="organizationId"
                                        :value="currentOrganization.id"
                                    />
                                    <input
                                        key="libraryItemId"
                                        hidden
                                        style="display: none"
                                        name="libraryItemId"
                                        :value="selectedLibraryItemId"
                                    />
                                    <input
                                        key="pageId"
                                        hidden
                                        style="display: none"
                                        name="pageId"
                                        :value="pageIDValidated"
                                    />
                                    <input
                                        v-for="key in bulkExportFieldKeys"
                                        :key="key"
                                        hidden
                                        style="display: none"
                                        :name="key"
                                        :value="reportQuery[key]"
                                    />
                                    <button class="btn btn-light btn-sm" type="submit" :disabled="bulkExportIsDisabled">
                                        Bulk Data Export
                                    </button>
                                </form>
                            </div>
                            <button class="btn btn-primary btn-sm" @click="fetchReport()" :disabled="submitDisabled">
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
                <br />
                <hr />
                <br />
                <div v-if="reportQuery.reportType == 'caseCompletion'">
                    <completion-report
                        ref="completionReport"
                        :library-item-id="selectedLibraryItemId"
                        :launch-request-id="ltiLaunchRequestId"
                        :start-date="reportQuery.startDate"
                        :end-date="reportQuery.endDate"
                        :include-share-links="reportQuery.includeShareLinks"
                        :page-id="pageIDValidated"
                    ></completion-report>
                </div>
                <div v-else-if="reportQuery.reportType == 'choiceResponse'">
                    <choice-response-report
                        ref="choiceResponseReport"
                        :library-item-id="selectedLibraryItemId"
                        :launch-request-id="ltiLaunchRequestId"
                        :start-date="reportQuery.startDate"
                        :end-date="reportQuery.endDate"
                        :include-share-links="reportQuery.includeShareLinks"
                        :panel-id="reportQuery.panelId"
                        :first-response-only="reportQuery.firstResponseOnly"
                        :page-id="pageIDValidated"
                        :disable-editor-links="selectedItemIsCommunityRef"
                    ></choice-response-report>
                </div>
                <div v-else-if="reportQuery.reportType == 'textResponse'">
                    <text-response-report
                        ref="textResponseReport"
                        :library-item-id="selectedLibraryItemId"
                        :launch-request-id="ltiLaunchRequestId"
                        :start-date="reportQuery.startDate"
                        :end-date="reportQuery.endDate"
                        :include-share-links="reportQuery.includeShareLinks"
                        :panel-id="reportQuery.panelId"
                        :page-id="pageIDValidated"
                        :disable-editor-links="selectedItemIsCommunityRef"
                    ></text-response-report>
                </div>
                <div v-else-if="reportQuery.reportType == 'timerPerformance'">
                    <timer-performance-report
                        ref="timerPerformanceReport"
                        :library-item-id="selectedLibraryItemId"
                        :launch-request-id="ltiLaunchRequestId"
                        :start-date="reportQuery.startDate"
                        :end-date="reportQuery.endDate"
                        :include-share-links="reportQuery.includeShareLinks"
                        :timer-id="reportQuery.timerID"
                        :page-id="pageIDValidated"
                        :disable-editor-links="selectedItemIsCommunityRef"
                    ></timer-performance-report>
                </div>
                <div v-else-if="reportQuery.reportType == 'sessionList'">
                    <session-list-report
                        ref="sessionListReport"
                        :library-item-id="selectedLibraryItemId"
                        :launch-request-id="ltiLaunchRequestId"
                        :start-date="reportQuery.startDate"
                        :end-date="reportQuery.endDate"
                        :page-id="pageIDValidated"
                        :include-share-links="reportQuery.includeShareLinks"
                    ></session-list-report>
                </div>
                <br />
            </div>
        </div>
        <!-- Info Modal -->
        <base-modal id="info-modal" :centered="true" size="large">
            <template slot="title"> Report Types </template>
            <template>
                <p class="mb-2 font-weight-500">
                    <span class="icon icon-glyphicons-basic-medal text-secondary mr-1"></span>
                    Completion Report
                </p>
                <p>
                    A summary showing which learners have attempted or completed a learning experience. Includes the number of
                    attempts, and the average score across all learners.
                </p>
                <hr />
                <p class="mb-2 font-weight-500">
                    <span class="icon icon-glyphicons-basic-stats-bars text-secondary mr-1"></span>
                    Choice Response Report
                </p>
                <p>
                    See how learners responded to a choice-based inquiry (e.g. branching, selection, image hotspots)
                    during a learning experience. Repeat attempts within the reporting time frame made by the same learner are
                    filtered from these results, favouring each learner's first attempt at the learning experience. Optionally run
                    the report using each learner's first response only.
                </p>
                <hr />
                <p class="mb-2 font-weight-500">
                    <span class="icon icon-glyphicons-basic-stopwatch text-secondary mr-1"></span>
                    Timer Peformance Report
                </p>
                <p>See how learners performed for a given timer. See which learners exceeded time limits.</p>
                <hr />
                <p class="mb-2 font-weight-500">
                    <span class="icon icon-glyphicons-basic-paragraph-left text-secondary mr-1"></span>
                    Text Response Report
                </p>
                <p>A list of all learner text responses, for a given text response inquiry.</p>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
            </template>
        </base-modal>
    </div>
</template>

<script>
import BaseForm from "@/components/BaseForm"
import BaseModal from "@/components/BaseModal"
import CompletionReport from "@/components/reports/CompletionReport"
import ChoiceResponseReport from "@/components/reports/ChoiceResponseReport"
import TextResponseReport from "@/components/reports/TextResponseReport"
import TimerPerformanceReport from "@/components/reports/TimerPerformanceReport"
import SessionListReport from "@/components/reports/SessionListReport"
import ReportTypeSelector from "@/components/reports/ReportTypeSelector"
import ReportPanelSelector from "@/components/reports/ReportPanelSelector"
import LibraryItemSelector from "@/components/LibraryItemSelector"
import http from "@/axios.js"

export default {
    name: "ReportView",
    components: {
        BaseForm,
        BaseModal,
        CompletionReport,
        ChoiceResponseReport,
        TextResponseReport,
        TimerPerformanceReport,
        SessionListReport,
        ReportTypeSelector,
        LibraryItemSelector,
        ReportPanelSelector,
    },
    data() {
        return {
            reportableCases: [],
            panels: [],
            timers: [],
            page: {},
            panel: {
                node: {},
            },
            selectedItem: null,
            reportQuery: {
                reportType: "sessionList",
                panelId: null,
                timerID: null,
                completeOnly: false,
                includeShareLinks: false,
                firstResponseOnly: false,
            },
            panelsLoading: false,
            timersLoading: false,
        }
    },
    computed: {
        loading() {
            return this.$store.getters.loading
        },
        selectedLibraryItemId() {
            if (this.selectedItem) {
                return this.selectedItem.id
            }
            return null
        },
        pageID() {
            return this.$route.params.pageId
        },
        pageIDValidated() {
            return this.pageID ? Number(this.pageID) : null
        },
        ltiLaunchRequestId() {
            return this.$route.params.launchRequestId
        },
        ltiLaunchRequestIdValidated() {
            return this.ltiLaunchRequestId ? Number(this.ltiLaunchRequestId) : null
        },
        // Returns true if the current route contains a page ID. Page Reports and the main
        // Reports view share the same vue component, with slight differences in functionality.
        isViewingPageReports() {
            return Boolean(this.pageIDValidated)
        },
        isViewingLtiReports() {
            return this.$route.name === "LtiLaunchRequestReports"
        },
        showLibraryItemSelector() {
            // Hide the selector only when viewing LTI reports.
            return !this.isViewingLtiReports
        },
        currentOrganization() {
            return this.$store.getters.currentOrganization
        },
        selectedReportPanelOptions() {
            let applicablePanelTypes = []
            if (this.reportQuery.reportType == "choiceResponse") {
                applicablePanelTypes = ["multichoice", "multiselect", "singleselect", "image_hotspots"]
            } else if (this.reportQuery.reportType == "textResponse") {
                applicablePanelTypes = ["textarea"]
            }
            return this.panels.filter((p) => applicablePanelTypes.includes(p.panel_type))
        },
        reportFields() {
            let vm = this
            return [
                {
                    name: "Timer",
                    key: "timerID",
                    required: true,
                    type: "search",
                    options: vm.timers,
                    labelField: "label",
                    valueField: "id",
                    searchFields: ["label"],
                    maxItems: 1,
                    placeholder: "Search for a timer",
                    hidden(value) {
                        return value.reportType != "timerPerformance"
                    },
                    loading: vm.timersLoading,
                    subtext:
                        vm.timers.length == 0 && vm.selectedLibraryItemId
                            ? "No timers have been added to this learning experience yet"
                            : null,
                },
                {
                    name: "Start Date",
                    key: "startDate",
                    type: "date",
                    required: true,
                },
                {
                    name: "End Date",
                    key: "endDate",
                    type: "date",
                    required: true,
                },
                {
                    name: "Only include each learner's first response",
                    key: "firstResponseOnly",
                    type: "checkbox",
                    required: false,
                    hidden(value) {
                        return value.reportType != "choiceResponse"
                    },
                    positionIndent: true,
                },
                {
                    name: "Include learner activity from Share Links",
                    description:
                        "When checked, activity from learners accessing the learning experience via Share Links with Learner Reporting enabled will be included.",
                    key: "includeShareLinks",
                    type: "checkbox",
                    required: false,
                    positionIndent: true,
                    hidden() {
                        return vm.isViewingPageReports || vm.isViewingLtiReports
                    },
                },
            ]
        },
        bulkExportFieldKeys() {
            let fields = this.reportFields.map((f) => f.key)
            delete fields.panelId
            return fields
        },
        showBulkExportBtn() {
            return this.reportQuery.reportType === "choiceResponse"
        },
        bulkExportIsDisabled() {
            return this.loading || !this.selectedLibraryItemId
        },
        // Determines whether the report query is valid.
        reportQueryValid() {
            let libraryItemIsSelected = this.showLibraryItemSelector ? Boolean(this.selectedLibraryItemId) : true
            if (this.inquiryReportSelected) {
                return libraryItemIsSelected && Boolean(this.reportQuery.panelId)
            } else if (this.reportQuery.reportType == "timerPerformance") {
                return libraryItemIsSelected && Boolean(this.reportQuery.timerID)
            }
            return libraryItemIsSelected
        },
        submitDisabled() {
            return this.loading || !this.reportQueryValid
        },
        inquiryReportSelected() {
            return ["choiceResponse", "textResponse"].includes(this.reportQuery.reportType)
        },
        selectedItemIsCommunityRef() {
            return this.selectedItem && this.selectedItem.item_type === "community_reference"
        },
        selectedReportType() {
            return this.reportQuery.reportType
        },
        formFilterDataUrl() {
            if (this.selectedLibraryItemId) {
                return `/api/reports/library-items/${this.selectedLibraryItemId}/`
            } else {
                return `/api/reports/lti-launch-requests/${this.ltiLaunchRequestId}/`
            }
        },
    },
    methods: {
        async fetchData() {
            if (this.pageID) {
                // Fetch details about the page.
                let vm = this
                http.get(`/api/pages/${this.pageID}/`)
                    .then((response) => {
                        vm.page = response.data
                    })
                    .finally(() => {
                        vm.$store.dispatch("removeLoad")
                    })
            }
            if (this.showLibraryItemSelector) {
                // Fetch an initial set of items.
                this.$refs.itemSelector.searchDebounced("")
            }
        },
        fetchCaseInfo() {
            this.panelsLoading = true
            let vm = this
            http.get(this.formFilterDataUrl, {
                params: {
                    panelType: "multichoice,multiselect,singleselect,image_hotspots,textarea",
                    activeOnly: true,
                },
            })
                .then(function (response) {
                    vm.panels = response.data.panels
                    vm.timers = response.data.node_timers
                })
                .finally(function () {
                    vm.panelsLoading = false
                })
        },
        fetchReport() {
            if (this.reportQuery.reportType == "caseCompletion") {
                this.$refs.completionReport.refresh()
            } else if (this.reportQuery.reportType == "choiceResponse") {
                this.$refs.choiceResponseReport.refresh()
            } else if (this.reportQuery.reportType == "textResponse") {
                this.$refs.textResponseReport.refresh()
            } else if (this.reportQuery.reportType == "timerPerformance") {
                this.$refs.timerPerformanceReport.refresh()
            } else if (this.reportQuery.reportType == "sessionList") {
                this.$refs.sessionListReport.refresh()
            }
        },
        openInfoModal() {
            $("#info-modal").modal("show")
        },
    },
    watch: {
        selectedLibraryItemId(newVal) {
            if (!newVal) {
                return
            }
            this.fetchCaseInfo()
            this.reportQuery.panelId = null
            this.reportQuery.timerID = null
        },
        selectedReportType() {
            // Clear the selected panel and timer when the report type changes.
            this.reportQuery.panelId = null
            this.reportQuery.timerID = null
        },
    },
    mounted() {
        this.fetchData()
        if (this.isViewingPageReports) {
            // Always include share links when viewing page reports.
            this.reportQuery.includeShareLinks = true
        }
        if (this.isViewingLtiReports) {
            // Fetch initial info to be used for form filters.
            this.fetchCaseInfo()
        }
    },
}
</script>

<style lang="scss" scoped>
.report-container {
    max-width: 1450px;
}
</style>
